/**
 * 技術面接問題 (/interview-questions) の自分で採点した記録。
 *
 * 記録はこの端末のブラウザ (localStorage) だけに残す。ログインして残す形はまだ無い。
 * 1 問に残すのは最後の採点 1 つ (言えた、あやしい、言えなかった) と、採点した時刻だけ。
 * 書いた答えの文は残さない。
 *
 * 「済んだ」は「言えた」と採点した問いだけを数える。「あやしい」と「言えなかった」は解き直しの側に入るので、
 * 1 つの問いが済んだ数と解き直しの両方に数えられることはない
 */
import type { InterviewDomainId, InterviewMeta } from "@/lib/interview-questions";

export const INTERVIEW_GRADES = [
  { id: "said", label: "言えた" },
  { id: "shaky", label: "あやしい" },
  { id: "missed", label: "言えなかった" },
] as const;

export type InterviewGrade = (typeof INTERVIEW_GRADES)[number]["id"];

export type InterviewAnswerRecord = { grade: InterviewGrade; at: number };

export type InterviewAnswerLog = Record<string, InterviewAnswerRecord>;

export const INTERVIEW_ANSWER_LOG_KEY = "yumesaku:interview-answers:v1";

function isGrade(value: unknown): value is InterviewGrade {
  return INTERVIEW_GRADES.some((grade) => grade.id === value);
}

export function interviewGradeLabel(grade: InterviewGrade) {
  return INTERVIEW_GRADES.find((g) => g.id === grade)!.label;
}

/** 保存された文字列を記録に戻す。壊れた値や知らない採点は捨て、読めた分だけを返す */
export function parseAnswerLog(raw: string | null): InterviewAnswerLog {
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};
  const log: InterviewAnswerLog = {};
  for (const [slug, value] of Object.entries(parsed)) {
    if (typeof value !== "object" || value === null) continue;
    const { grade, at } = value as { grade?: unknown; at?: unknown };
    if (!isGrade(grade) || typeof at !== "number" || !Number.isFinite(at)) continue;
    log[slug] = { grade, at };
  }
  return log;
}

/** 1 問の採点を記録に入れる。前の採点は上書きする (最後の採点だけが残る) */
export function recordGrade(log: InterviewAnswerLog, slug: string, grade: InterviewGrade, at: number): InterviewAnswerLog {
  return { ...log, [slug]: { grade, at } };
}

/** 解き直す問い。「あやしい」と「言えなかった」だけを、一覧と同じ order の順で返す。記録にあっても今は無い問いは出さない */
export function retryEntries<T extends Pick<InterviewMeta, "slug" | "order">>(log: InterviewAnswerLog, entries: T[]): T[] {
  return entries
    .filter((entry) => {
      const grade = log[entry.slug]?.grade;
      return grade === "shaky" || grade === "missed";
    })
    .sort((a, b) => a.order - b.order);
}

/** 分野ごとの「言えた」の数。今ある問いだけを数え、記録の無い分野は 0 */
export function saidCountByDomain(
  log: InterviewAnswerLog,
  entries: Pick<InterviewMeta, "slug" | "domain">[],
): Partial<Record<InterviewDomainId, number>> {
  const counts: Partial<Record<InterviewDomainId, number>> = {};
  for (const entry of entries) {
    counts[entry.domain] = (counts[entry.domain] ?? 0) + (log[entry.slug]?.grade === "said" ? 1 : 0);
  }
  return counts;
}

/* ---- ブラウザに読み書きする薄い層。使えない端末 (保存を止めた設定、容量の上限) でも頁は止めない ---- */

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export type AnswerLogState = { status: "ready"; log: InterviewAnswerLog } | { status: "unavailable" };

const PROBE_KEY = `${INTERVIEW_ANSWER_LOG_KEY}:probe`;

/** 読めて書けるかを先に試してから記録を読む。どちらかで落ちたら unavailable */
export function readAnswerLog(storage: StorageLike | null | undefined): AnswerLogState {
  if (!storage) return { status: "unavailable" };
  try {
    storage.setItem(PROBE_KEY, "1");
    storage.removeItem(PROBE_KEY);
    return { status: "ready", log: parseAnswerLog(storage.getItem(INTERVIEW_ANSWER_LOG_KEY)) };
  } catch {
    return { status: "unavailable" };
  }
}

/** 採点を書く。書けたら新しい記録を、書けなければ null を返す (呼ぶ側は「残せなかった」と出す) */
export function saveGrade(
  storage: StorageLike | null | undefined,
  slug: string,
  grade: InterviewGrade,
  at: number,
): InterviewAnswerLog | null {
  if (!storage) return null;
  try {
    const next = recordGrade(parseAnswerLog(storage.getItem(INTERVIEW_ANSWER_LOG_KEY)), slug, grade, at);
    storage.setItem(INTERVIEW_ANSWER_LOG_KEY, JSON.stringify(next));
    return next;
  } catch {
    return null;
  }
}

/** window.localStorage を触るだけで投げる端末がある (保存を止めた設定) ので、取り出しも包む */
export function browserStorage(): StorageLike | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}
